import { api } from './client';
import type { ApiResponse } from './types';

// A business's reach: its season, where its contacts come from, answering
// asks as the business, "did they help?" and the monthly receipt.

export interface ProviderSeason {
  kind: string;
  name: string;
  festivalSlug: string | null;
  startsOn: string;
  endsOn: string | null;
  daysAway: number;
  sourceNote: string;
}

export interface ProviderSeasonRead {
  category: string;
  today: string;
  timeZone: string;
  season: ProviderSeason | null;
}

export const CONTACT_SOURCES = [
  'search', 'directory', 'answer', 'vouch_link', 'home', 'event', 'haat', 'follow', 'direct',
] as const;
export type ContactSource = (typeof CONTACT_SOURCES)[number];
export const TAP_KINDS = ['call', 'whatsapp', 'directions', 'website'] as const;
export type TapKind = (typeof TAP_KINDS)[number];

export interface ContactTap {
  id: string;
  kind: string;
  source: string | null;
  counted: boolean;
  createdAt: string;
}

export interface ProviderAsk {
  id: string;
  title: string;
  city: string | null;
  createdAt: string;
  answerCount: number;
  answeredByThisBusiness: boolean;
}

export interface ProviderAskList {
  items: ProviderAsk[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export type OutcomeAnswer = 'yes' | 'not_yet' | 'no';

export interface ThreadOutcome {
  confirmedWork: boolean;
  answer: string | null;
  canAnswer: boolean;
}

export interface ProviderReceipt {
  providerId: string;
  month: string;
  timeZone: string;
  newPeopleInTouch: {
    total: number;
    byKind: { message: number; call: number; whatsapp: number; directions: number };
    threadIds: string[];
  };
  confirmedWork: { total: number; threadIds: string[] };
  cameBack: { total: number; threadIds: string[] };
  sources: { source: string; count: number }[];
  taps: { call: number; whatsapp: number; directions: number; website: number };
  signals: { pageViews: number; newFollowers: number };
  outsideWeDesiNotCounted: true;
}

const segment = encodeURIComponent;

export const providerReach = {
  /** "What WeDesi brought you" for one month (YYYY-MM). Owner or manager. */
  receipt: (providerId: string, month: string) =>
    api.get<ApiResponse<ProviderReceipt>>(`/providers/${segment(providerId)}/receipt`, { month }),
  /** "Did they help?" as the caller's side of the conversation may see it. */
  threadOutcome: (threadId: string) =>
    api.get<ApiResponse<{ outcome: ThreadOutcome }>>(`/provider-messages/${segment(threadId)}`, { limit: 1 }),
  /** The member's answer, once asked. */
  answerOutcome: (threadId: string, answer: OutcomeAnswer, idempotencyKey: string) =>
    api.post<ApiResponse<ThreadOutcome & { offerVouch: boolean }>>(
      `/provider-messages/${segment(threadId)}/outcome`,
      { answer, idempotencyKey },
    ),
  /** The open asks a business may answer as itself. Owner or manager. */
  asks: (providerId: string, page = 1, limit = 20) =>
    api.get<ApiResponse<ProviderAskList>>(`/providers/${segment(providerId)}/asks`, { page, limit }),
  /** Answer an ask as the business. */
  answerAsBusiness: (questionId: string, providerId: string, body: string) =>
    api.post<ApiResponse<{ id: string }>>(`/questions/${segment(questionId)}/answers`, { body, asProviderId: providerId }),
  /** Stop, or start again, seeing one business's answers. */
  hideAnswers: (providerId: string) =>
    api.post<ApiResponse<{ providerId: string; hidden: boolean }>>(`/providers/${segment(providerId)}/hide-answers`),
  showAnswers: (providerId: string) =>
    api.delete<ApiResponse<{ providerId: string; hidden: boolean }>>(`/providers/${segment(providerId)}/hide-answers`),
  /** A tap to call, WhatsApp, ask the way or open the website. Sign-in optional. */
  contactTap: (providerId: string, kind: TapKind, source?: ContactSource) =>
    api.post<ApiResponse<ContactTap>>(`/providers/${segment(providerId)}/contact-taps`, { kind, source }),
  /** Message a business, saying which door the member came through. */
  messageWithDoor: (providerId: string, body: string, source?: ContactSource) =>
    api.post<ApiResponse<{ threadId: string }>>(`/providers/${segment(providerId)}/messages`, { body, source }),
  /** The next season for a trade, on the reader's calendar. Public. */
  season: (category: string, tz: string) =>
    api.get<ApiResponse<ProviderSeasonRead>>('/reference/provider-seasons', { category, tz }),
  /** The owner dashboard's `season`, read from the dashboard itself. */
  dashboardSeason: (providerId: string) =>
    api.get<ApiResponse<{ season: ProviderSeason | null }>>(`/providers/${segment(providerId)}/dashboard`),
};
